import React from 'react';
import { Phone } from 'lucide-react';
import FormField, { fieldClass } from './FormField';
import { COUNTRY_CODES, countryFor, digitsOnly, maxMobileLength } from '../../utils/bookingValidation';

/**
 * Mobile number with a country dial code.
 *
 * Stations get foreign travellers, so the code is selectable — but +91 stays the default
 * because the overwhelming majority of guests are domestic. The number input accepts digits
 * only and caps its length at whatever the selected country allows, so a wrong number is
 * caught while it is being typed rather than at submit.
 */
const PhoneInput = ({
  countryCode,
  mobile,
  onChange,
  error,
  disabled = false,
  label = 'Mobile Number',
  required = true,
  id = 'mobile'
}) => {
  const country = countryFor(countryCode);
  const maxLength = maxMobileLength(countryCode);

  const handleCodeChange = (e) => {
    const nextCode = e.target.value;
    // Trim the number to the new country's limit rather than submitting an over-long one.
    onChange({ countryCode: nextCode, mobile: digitsOnly(mobile).slice(0, maxMobileLength(nextCode)) });
  };

  const handleNumberChange = (e) => {
    onChange({ countryCode, mobile: digitsOnly(e.target.value).slice(0, maxLength) });
  };

  return (
    <FormField
      label={label}
      htmlFor={id}
      required={required}
      error={error}
      hint={`${country.name} · ${country.nsn ? country.nsn.join(' or ') : 'up to 15'} digits`}
    >
      <div className={`flex rounded-lg border-2 bg-white transition-all duration-200 focus-within:ring-2 ${
        error
          ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-500/20'
          : 'border-midnight-200 focus-within:border-railway-500 focus-within:ring-railway-500/20'
      }`}>
        <select
          aria-label="Country dial code"
          value={countryCode}
          onChange={handleCodeChange}
          disabled={disabled}
          className="pl-3 pr-1 py-3 bg-transparent text-midnight-900 font-medium rounded-l-lg focus:outline-none cursor-pointer disabled:cursor-not-allowed max-w-[7.5rem]"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code + c.iso} value={c.code}>
              {c.flag} {c.code}
            </option>
          ))}
        </select>

        <span className="self-center h-6 w-px bg-midnight-200" aria-hidden="true" />

        <div className="relative flex-1">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
          <input
            id={id}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={mobile}
            onChange={handleNumberChange}
            disabled={disabled}
            maxLength={maxLength}
            placeholder={country.code === '+91' ? '9876543210' : 'Mobile number'}
            className="w-full pl-9 pr-3 py-3 bg-transparent rounded-r-lg text-midnight-900 focus:outline-none disabled:cursor-not-allowed"
          />
        </div>
      </div>
    </FormField>
  );
};

export { fieldClass };
export default PhoneInput;
